import { z } from 'zod';
import { CreateDemandAttachmentSchema, DemandAttachmentSchema } from './demand-attachment.dto';
import { CreateDemandReviewerSchema } from './demand-reviewer.dto';
import { inferApiResultSchema } from './infer-api';

export const DemandEvidenceSchema = z.object({
  id: z.number(),
  demandId: z.number(),
  /** Exactly one kind reference is set. A third kind - market signals - is expected to follow. */
  demandAttachmentId: z.number().nullable(),
  ideaId: z.number().nullable(),
  /**
   * Who is responsible for reviewing this item. A reference to the demand's reviewer, so removing that
   * reviewer nulls this rather than stranding the evidence.
   *
   * Responsibility only - the reviewer's approve/reject is recorded against them for the whole demand,
   * not per evidence item, so this cannot tell you whether this item has been reviewed.
   */
  demandReviewerId: z.number().nullable(),
  /** Null for rows backfilled from links made before the audit columns existed. */
  addedBy: z.string().max(50).nullable(),
  createdAt: z.date(),
});
export type DemandEvidenceDto = z.infer<typeof DemandEvidenceSchema>;

/** Evidence with its payload resolved, so a client can render a row without a second request. */
export const DemandEvidenceDetailSchema = DemandEvidenceSchema.extend({
  attachment: DemandAttachmentSchema.nullable(),
});
export type DemandEvidenceDetailDto = z.infer<typeof DemandEvidenceDetailSchema>;

export const GetDemandEvidenceResponseSchema = inferApiResultSchema(
  z.array(DemandEvidenceDetailSchema),
);
export type GetDemandEvidenceResponseDto = z.infer<typeof GetDemandEvidenceResponseSchema>;

/**
 * The reviewer is named as a *person*, not chosen from the demand's existing reviewers: supplying one
 * creates a reviewer on the demand. Naming someone who already reviews it is a conflict, not a reuse.
 */
export const CreateDemandEvidenceSchema = z
  .object({
    // demandId comes from the path and userId from the request context, so neither is accepted here.
    attachment: CreateDemandAttachmentSchema.omit({ demandId: true, userId: true }).optional(),
    ideaId: z.number().optional(),
    reviewer: CreateDemandReviewerSchema.optional(),
  })
  .refine((dto) => [dto.attachment, dto.ideaId].filter((kind) => kind !== undefined).length === 1, {
    message: 'Provide exactly one of attachment or ideaId',
  });
export type CreateDemandEvidenceDto = z.infer<typeof CreateDemandEvidenceSchema>;

export const CreateDemandEvidenceResponseSchema = inferApiResultSchema(DemandEvidenceDetailSchema);
export type CreateDemandEvidenceResponseDto = z.infer<typeof CreateDemandEvidenceResponseSchema>;
